import React, { useEffect, useState } from 'react';
import { shareACP } from '@api/resident';
import { Recipient, SharingACPDetails } from '@typings/ACPTypes';
import { ApiResponse } from '@typings/ApiResponse';
import { ResidentDetails } from '@typings/ResidentTypes';
import { Role } from '@typings/ProfileTypes';
import { FormError, SubmitStatus, SubmitStatusEnum } from '@typings/common';
import {
  updateErrors,
  getErrorMessage as getErrorMessageFromErrors,
  isValidEmail,
  capitalizeName,
} from '@utils/commons';
import NewErrorSummary from '@components/NewErrorSummary';
import WarningText from '@components/WarningText';
import getErrorMessage from '@utils/errorUtils';
import SuccessNotificationBanner from '@components/SuccessNotificationBanner';
import { SharingACPTypes } from './utils';

export interface NextOfKinDetails {
  fullName: string;
  relationship: string;
}
interface SharingPrintedCopiesProps {
  role: string;
  userId: string;
  residentDetails: ResidentDetails | undefined;
  onFormSubmit: () => void;
  nextOfKinDetails: NextOfKinDetails;
}

const SharingPrintedCopies: React.FC<SharingPrintedCopiesProps> = ({
  role,
  userId,
  residentDetails,
  onFormSubmit,
  nextOfKinDetails,
}) => {
  const [residentFullName, setResidentFullName] = useState('');
  const [recipientInfo, setRecipientInfo] = useState<Recipient>({
    name: '',
    relationship: '',
    email: '',
  });
  const [errors, setErrors] = useState<FormError[]>([]);

  const [assignInfo, setAssignInfo] = useState<{
    hasNextOfKin: boolean;
    isDisabled: boolean;
    isChecked: boolean;
  }>({
    hasNextOfKin: false,
    isDisabled: false,
    isChecked: false,
  });
  const [sharingPrintedCopiesStatus, setSharingPrintedCopiesStatus] =
    useState<SubmitStatus>({
      loading: false,
      submitted: SubmitStatusEnum.IDLE,
      message: '',
    });

  const [errorMessages, setErrorMessages] = useState<{
    fullName: string | undefined;
    email: string | undefined;
    relationship: string | undefined;
  }>({
    fullName: '',
    email: '',
    relationship: '',
  });

  useEffect(() => {
    if (residentDetails) {
      const {
        profile: { full_name: fullName },
      } = residentDetails;
      setResidentFullName(fullName);
    }
  }, [residentDetails]);

  // Update input and dynamically validate fields
  const handleInputChange = (
    field: 'name' | 'relationship' | 'email',
    value: string
  ) => {
    setRecipientInfo(prevState => ({ ...prevState, [field]: value }));
  };

  const onFinish = async (e: any) => {
    e.preventDefault();

    let validationErrors: FormError[] = [];

    validationErrors = updateErrors(
      validationErrors,
      'spc-full-name',
      !recipientInfo.name.trim() ? 'Please enter their full name' : null
    );
    validationErrors = updateErrors(
      validationErrors,
      'spc-relationship',
      !recipientInfo.relationship.trim()
        ? 'Please enter their relationship'
        : null
    );
    validationErrors = updateErrors(
      validationErrors,
      'spc-email',
      !isValidEmail(recipientInfo.email) ? 'Please enter a valid email' : null
    );
    setErrors(prev => validationErrors);

    if (validationErrors.length !== 0) {
      return;
    }

    setSharingPrintedCopiesStatus(prev => ({ ...prev, loading: true }));

    try {
      const response: ApiResponse<SharingACPDetails> = await shareACP({
        ...recipientInfo,
        next_of_kin: assignInfo.isChecked,
        carehome_user_id: userId,
        type: SharingACPTypes.PRINT,
      });
      if (response?.status === 'success') {
        setRecipientInfo({ name: '', relationship: '', email: '' });
        onFormSubmit();
        setAssignInfo(prev => ({
          ...prev,
          isChecked: false,
        }));
        setSharingPrintedCopiesStatus(prev => ({
          ...prev,
          submitted: SubmitStatusEnum.SUCCESS,
          message: 'This Advance Care Plan has been sent',
        }));
        setTimeout(() => {
          setSharingPrintedCopiesStatus(prev => ({
            ...prev,
            submitted: SubmitStatusEnum.IDLE,
            message: '',
          }));
        }, 3000);
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setSharingPrintedCopiesStatus({
        loading: false,
        submitted: SubmitStatusEnum.FAILED,
        message: errorMessage,
      });
    } finally {
      setSharingPrintedCopiesStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const handleCheckboxChange = async (e: any) => {
    const { checked } = e.target;

    if (checked) {
      if (assignInfo.hasNextOfKin) {
        setRecipientInfo({
          ...recipientInfo,
          name: nextOfKinDetails.fullName || recipientInfo.name,
          relationship:
            nextOfKinDetails.relationship || recipientInfo.relationship,
        });
        setAssignInfo({ ...assignInfo, isChecked: checked });
      } else {
        setAssignInfo(prev => ({ ...prev, isChecked: true }));
        setTimeout(() => {
          setAssignInfo(prev => ({
            ...prev,
            isChecked: false,
            isDisabled: true,
          }));
        }, 500);
      }
    } else if (!checked) {
      setAssignInfo(prev => ({
        ...prev,
        isChecked: false,
      }));
      if (assignInfo.hasNextOfKin) {
        setRecipientInfo({
          ...recipientInfo,
          name: '',
          relationship: '',
        });
      }
    }
  };

  useEffect(() => {
    setAssignInfo({
      ...assignInfo,
      hasNextOfKin:
        !!nextOfKinDetails.fullName && !!nextOfKinDetails.relationship,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextOfKinDetails]);

  useEffect(() => {
    setErrorMessages({
      fullName: getErrorMessageFromErrors(errors, 'spc-full-name'),
      email: getErrorMessageFromErrors(errors, 'spc-email'),
      relationship: getErrorMessageFromErrors(errors, 'spc-relationship'),
    });
  }, [errors]);

  return (
    <div className="mt-5 govuk-bp:mt-6">
      <h1 className="govuk-heading-l">Sharing printed copies</h1>
      {sharingPrintedCopiesStatus.submitted === SubmitStatusEnum.SUCCESS && (
        <SuccessNotificationBanner
          title={sharingPrintedCopiesStatus.message}
          duration={3000}
          noScrollToTop
        />
      )}
      <p className="govuk-body">
        Each time a paper copy of this Advance Care Plan is provided to someone
        the recipients information should be added in the fields below.
      </p>
      <form>
        {errors.length > 0 && <NewErrorSummary errors={errors} />}
        {sharingPrintedCopiesStatus.submitted === SubmitStatusEnum.FAILED && (
          <NewErrorSummary
            errors={[
              {
                id: 'sharingByEmailAPI',
                message: sharingPrintedCopiesStatus.message,
              },
            ]}
          />
        )}
        <div className="govuk-form-group">
          <fieldset
            className="govuk-fieldset"
            aria-describedby="asigned-nok-hint"
          >
            <div className="govuk-checkboxes" data-module="govuk-checkboxes">
              <div className="govuk-checkboxes__item">
                <input
                  className="govuk-checkboxes__input"
                  id="asigned-nok"
                  name="asigned-nok"
                  type="checkbox"
                  checked={assignInfo.isChecked}
                  disabled={assignInfo.isDisabled}
                  onChange={handleCheckboxChange}
                />
                <label
                  className="govuk-label govuk-checkboxes__label"
                  htmlFor="asigned-nok"
                >
                  If this person is the assigned Next of Kin for{' '}
                  {residentFullName ? capitalizeName(residentFullName) : '...'}{' '}
                  check the box.
                </label>
              </div>
            </div>
          </fieldset>
          {assignInfo.isDisabled && (
            <WarningText content="A Next of Kin has not been added to this care plan" />
          )}
        </div>
        {/* {isShowFormErrors && <ErrorSummary errors={recipientFormErrors} />} */}
        <div
          className={`govuk-form-group ${errorMessages.fullName ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="spc-full-name">
            Recipient&apos;s full name
          </label>
          <div className="govuk-hint" id="full-name-hint" />
          {errorMessages.fullName && (
            <p id="full-name-input-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>
              {errorMessages.fullName}
            </p>
          )}
          <input
            className={`govuk-input govuk-input--width-20 ${errorMessages.fullName ? 'govuk-input--error' : ''}`}
            id="spc-full-name"
            name="name"
            type="text"
            disabled={assignInfo.isChecked}
            aria-describedby={
              errorMessages.fullName ? 'full-name-input-error' : undefined
            }
            autoComplete="name"
            value={recipientInfo.name}
            onChange={e => handleInputChange('name', e.target.value)}
          />
        </div>
        <div
          className={`govuk-form-group ${errorMessages.relationship ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="spc-relationship">
            Recipient&apos;s relationship
          </label>
          <div className="govuk-hint" id="relationship-hint" />
          {errorMessages.relationship && (
            <p id="full-name-input-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>
              {errorMessages.relationship}
            </p>
          )}
          <input
            className={`govuk-input govuk-input--width-20 ${errorMessages.relationship ? 'govuk-input--error' : ''}`}
            id="spc-relationship"
            name="relationship"
            type="text"
            disabled={assignInfo.isChecked}
            aria-describedby={
              errorMessages.relationship
                ? 'relationship-input-error'
                : undefined
            }
            autoComplete="relationship"
            value={recipientInfo.relationship}
            onChange={e => handleInputChange('relationship', e.target.value)}
          />
        </div>
        <div
          className={`govuk-form-group ${errorMessages.email ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="spc-email">
            Recipient&apos;s email address
          </label>
          {errorMessages.email && (
            <p id="full-name-input-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>
              {errorMessages.email}
            </p>
          )}
          <input
            className={`govuk-input govuk-input--width-20 ${errorMessages.email ? 'govuk-input--error' : ''}`}
            id="spc-email"
            name="email"
            type="email"
            spellCheck={false}
            aria-describedby="email-hint"
            autoComplete="email"
            value={recipientInfo.email}
            onChange={e => handleInputChange('email', e.target.value)}
          />
        </div>

        <button
          type="submit"
          disabled={
            sharingPrintedCopiesStatus.loading ||
            role === Role.TeamMemberViewOnly
          }
          className="govuk-button govuk-!-margin-bottom-0"
          onClick={onFinish}
        >
          Save
        </button>
      </form>
    </div>
  );
};

export default SharingPrintedCopies;
